import type { ComponentType } from "react";
import { createBrowserRouter, Navigate, useParams, useSearchParams } from "react-router";
import { AdminShell } from "@/components/layout/admin-shell";
import { AppShell } from "@/components/layout/app-shell";
import { GuestOnly, RequireAdmin, RequireAuth } from "@/components/layout/guards";
import { Backdrop } from "@/components/ui/backdrop";
import { Button } from "@/components/ui/button";
import { Loading } from "@/components/ui/states";
import NotFoundPage from "@/routes/not-found";
import { LazyOutlet } from "./lazy-outlet";

// 页面按路由懒加载（管理后台、2D 回放不进首屏包）。
const page = (load: () => Promise<{ default: ComponentType }>) => async () => ({ Component: (await load()).default });

function RouteError() {
  return (
    <div className="app cs-checker relative isolate items-center justify-center overflow-hidden p-6" role="alert">
      <Backdrop art="error-bg" className="page-art" />
      <div className="flex max-w-[420px] flex-col items-center gap-4 text-center">
        <h1 className="display-lg">页面加载失败</h1>
        <p className="page-sub">刷新一下试试。</p>
        <Button variant="default" onClick={() => window.location.reload()}>
          刷新页面
        </Button>
      </div>
    </div>
  );
}

const common = { ErrorBoundary: RouteError, HydrateFallback: () => <Loading /> };

/** 计分板已并入 2D 回放页的对话框；旧链接跳到回放页并打开它。 */
function ScoreboardRedirect() {
  const { demoId = "" } = useParams();
  return <Navigate to={`/demos/${demoId}?scoreboard=1`} replace />;
}

/** 回合与击杀已并入「深度分析 › 历史数据分析」的分页；旧链接带上 demo 与回合跳过去。 */
function RoundsRedirect() {
  const { demoId = "" } = useParams();
  const [sp] = useSearchParams();
  const round = sp.get("round");
  return <Navigate to={`/analysis/history?demo=${demoId}&tab=rounds${round ? `&round=${round}` : ""}`} replace />;
}

/** 旧的 /analysis/rounds 保留查询参数跳到 /analysis/history。 */
function AnalysisRoundsRedirect() {
  const [sp] = useSearchParams();
  const qs = sp.toString();
  return <Navigate to={`/analysis/history${qs ? `?${qs}` : ""}`} replace />;
}

export const router = createBrowserRouter([
  { path: "/login", element: <GuestOnly><LazyOutlet load={() => import("@/routes/auth/login")} /></GuestOnly> },
  { path: "/register", element: <GuestOnly><LazyOutlet load={() => import("@/routes/auth/register")} /></GuestOnly> },
  { path: "/auth/steam", lazy: page(() => import("@/routes/auth/steam")), ...common },
  { path: "/forgot-password", lazy: page(() => import("@/routes/auth/forgot")), ...common },
  { path: "/reset-password", lazy: page(() => import("@/routes/auth/reset")), ...common },
  { path: "/verify-email", lazy: page(() => import("@/routes/auth/verify")), ...common },
  { path: "/terms", lazy: page(() => import("@/routes/legal/terms")), ...common },
  { path: "/privacy", lazy: page(() => import("@/routes/legal/privacy")), ...common },
  { path: "/report", lazy: page(() => import("@/routes/legal/report")), ...common },
  {
    element: <RequireAuth><AppShell /></RequireAuth>,
    ErrorBoundary: RouteError,
    children: [
      { index: true, element: <Navigate to="/demos" replace /> },
      { path: "/demos", lazy: page(() => import("@/routes/demos/library")) },
      { path: "/demos/:demoId", lazy: page(() => import("@/routes/replay/replay")) },
      { path: "/demos/:demoId/scoreboard", element: <ScoreboardRedirect /> },
      { path: "/demos/:demoId/rounds", element: <RoundsRedirect /> },
      { path: "/analysis", element: <Navigate to="/analysis/history" replace /> },
      { path: "/analysis/history", lazy: page(() => import("@/routes/analysis/history-page")) },
      { path: "/analysis/rounds", element: <AnalysisRoundsRedirect /> },
      { path: "/matches", lazy: page(() => import("@/routes/matches/matches")) },
      { path: "/matches/:matchId", lazy: page(() => import("@/routes/matches/match")) },
      { path: "/tactics", lazy: page(() => import("@/routes/tactics/library")) },
      { path: "/tactics/:tacticId", lazy: page(() => import("@/routes/tactics/editor")) },
      { path: "/teams", lazy: page(() => import("@/routes/team/teams")) },
      { path: "/teams/:teamId", lazy: page(() => import("@/routes/team/team")) },
      { path: "/invite/:code", lazy: page(() => import("@/routes/team/invite")) },
      { path: "/skins", lazy: page(() => import("@/routes/skins/skins")) },
      { path: "/settings", lazy: page(() => import("@/routes/settings/profile")) },
      { path: "/settings/security", lazy: page(() => import("@/routes/settings/security")) },
      { path: "/settings/notifications", lazy: page(() => import("@/routes/settings/notifications")) },
      { path: "/settings/steam", lazy: page(() => import("@/routes/steam-callback")) },
      { path: "/help", lazy: page(() => import("@/routes/help")) },
    ],
  },
  {
    path: "/admin",
    element: <RequireAuth><RequireAdmin><AdminShell /></RequireAdmin></RequireAuth>,
    ErrorBoundary: RouteError,
    children: [
      { index: true, lazy: page(() => import("@/routes/admin/overview")) },
      { path: "workers", lazy: page(() => import("@/routes/admin/workers")) },
      { path: "users", lazy: page(() => import("@/routes/admin/users")) },
      { path: "users/:userId", lazy: page(() => import("@/routes/admin/user")) },
      { path: "demos", lazy: page(() => import("@/routes/admin/demos")) },
      { path: "servers", lazy: page(() => import("@/routes/admin/servers")) },
      { path: "servers/:serverId", lazy: page(() => import("@/routes/admin/server")) },
      { path: "matches", lazy: page(() => import("@/routes/admin/matches")) },
      { path: "matches/:matchId", lazy: page(() => import("@/routes/admin/match")) },
      { path: "server-demos", lazy: page(() => import("@/routes/admin/server-demos")) },
      { path: "server-tasks", lazy: page(() => import("@/routes/admin/server-tasks")) },
      { path: "server-templates", lazy: page(() => import("@/routes/admin/server-templates")) },
      { path: "server-admins", lazy: page(() => import("@/routes/admin/server-admins")) },
      { path: "server-components", lazy: page(() => import("@/routes/admin/server-components")) },
      { path: "server-components/:component", lazy: page(() => import("@/routes/admin/server-component")) },
      { path: "audit", lazy: page(() => import("@/routes/admin/audit")) },
      { path: "sensitive-words", lazy: page(() => import("@/routes/admin/sensitive-words")) },
    ],
  },
  { path: "*", element: <NotFoundPage /> },
]);
